import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHouders } from '../api/api';
import { fmtId } from '../util/format';
import { useLijst, ZoekBalk, KopRij, GefilterdChips, FilterPaneel, markeer } from '../components/lijst';

/* De houderslijst op het lijstontwerp van 26-08-2026: zoekbalk, loepjes in de
   kop voor vrije tekst, en het type (Bedrijf/Persoon) als facet in het paneel.
   Zie components/lijst.js voor het patroon. */

const houderNaam = (h) => {
  if (h.type === 'RP') return h.naam || '';
  return [h.voornaam, h.tussenvoegsel, h.achternaam].filter(Boolean).join(' ');
};

const typeLabel = (h) => (h.type === 'RP' ? 'Bedrijf' : 'Persoon');

export default function Houders() {
  const [houders, setHouders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const lijst = useLijst({
    facetten: [
      { key: 'type', label: 'Type', waarde: (h) => [typeLabel(h)] },
    ],
    kopzoek: {
      naam: { label: 'Naam', tekst: houderNaam },
      kvk: { label: 'KvK', tekst: (h) => h.kvk_nummer || '' },
      plaats: { label: 'Plaats', tekst: (h) => h.plaats || '' },
    },
    zoek: (h) => [
      ['id', `${h.houder_id} ${fmtId(h.houder_id)}`],
      ['naam', houderNaam(h)],
      ['kvk', h.kvk_nummer || ''],
      ['plaats', h.plaats || ''],
      ['contact', `${h.emailadres || ''} ${h.telefoonnummer || ''}`],
    ],
    sorteer: {
      houder_id: (h) => h.houder_id,
      naam: houderNaam,
      type: typeLabel,
      kvk_nummer: (h) => h.kvk_nummer || '',
      plaats: (h) => h.plaats || '',
      contact: (h) => h.emailadres || h.telefoonnummer || '',
    },
  });

  useEffect(() => { laadAlles(); }, []);

  const laadAlles = async () => {
    setLoading(true);
    try {
      const res = await getHouders();
      setHouders(res.data);
    } finally {
      setLoading(false);
    }
  };

  const KOLOMMEN = [
    { key: 'houder_id', label: 'ID' },
    { key: 'naam', label: 'Naam', loep: 'naam' },
    { key: 'type', label: 'Type' },
    { key: 'kvk_nummer', label: 'KvK', loep: 'kvk' },
    { key: 'plaats', label: 'Plaats', loep: 'plaats' },
    { key: 'contact', label: 'Contact' },
  ];

  const gefilterd = lijst.filterEnSorteer(houders);
  const q = lijst.zoek.trim();

  return (
    <div className="main-inner">
      <div className="page-header">
        <div>
          <h1 className="page-title">Houders</h1>
          <p className="page-subtitle">
            {houders.length} {houders.length === 1 ? 'houder' : 'houders'}
            {lijst.filterActief && ` · ${gefilterd.length} na filter`}
          </p>
        </div>
        <div className="row">
          <button className="btn btn-primary" onClick={() => navigate('/beregening/houders/nieuw')}>
            Nieuwe houder
          </button>
        </div>
      </div>

      <ZoekBalk
        lijst={lijst}
        placeholder="Zoek op naam, KvK-nummer, plaats of contactgegevens"
        metPaneelKnop
      />
      <GefilterdChips lijst={lijst} />

      {loading ? (
        <div className="empty-state"><span className="spinner" /></div>
      ) : houders.length === 0 ? (
        <div className="empty-state">Geen houders.</div>
      ) : (
        <div className="table-wrap">
          <table className="table pl-tabel">
            <thead><KopRij lijst={lijst} kolommen={KOLOMMEN} /></thead>
            <tbody>
              {gefilterd.length === 0 ? (
                <tr><td colSpan={KOLOMMEN.length} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: 32 }}>
                  Geen rijen voldoen aan de filter.
                </td></tr>
              ) : gefilterd.map(h => {
                const t = lijst.zoekTreffer(h);
                const kz = lijst.kopzoek;
                const naam = houderNaam(h);
                return (
                  <tr key={h.houder_id} className="clickable"
                      onClick={() => navigate(`/beregening/houders/${h.houder_id}`)}>
                    <td><span className="mono mono-id">
                      {t.waar === 'id' ? markeer(fmtId(h.houder_id), q) : fmtId(h.houder_id)}
                    </span></td>
                    <td><strong>
                      {naam
                        ? (kz.naam ? markeer(naam, kz.naam)
                          : t.waar === 'naam' ? markeer(naam, q) : naam)
                        : <span className="pl-leeg">–</span>}
                    </strong></td>
                    <td>
                      <span
                        className={`chip chip-clickable ${h.type === 'RP' ? 'chip-accent' : ''}`}
                        title="Klik om op dit type te filteren"
                        onClick={(e) => { e.stopPropagation(); lijst.zetFilter('type', typeLabel(h)); }}
                      >{typeLabel(h)}</span>
                    </td>
                    <td className="pl-cel-mono">
                      {h.kvk_nummer
                        ? (kz.kvk ? markeer(h.kvk_nummer, kz.kvk)
                          : t.waar === 'kvk' ? markeer(h.kvk_nummer, q) : h.kvk_nummer)
                        : <span className="pl-leeg">–</span>}
                    </td>
                    <td>
                      {h.plaats
                        ? (kz.plaats ? markeer(h.plaats, kz.plaats)
                          : t.waar === 'plaats' ? markeer(h.plaats, q) : h.plaats)
                        : <span className="pl-leeg">–</span>}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>
                      {h.emailadres || h.telefoonnummer || <span className="pl-leeg">–</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <FilterPaneel lijst={lijst} rijen={houders} />
    </div>
  );
}
